import { default as srcDayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';

declare module 'dayjs' {
  interface Dayjs {
    toLocalTime(): srcDayjs.Dayjs;
    /**
     * Temporarily update the global locale configuration for the given callback.
     *
     * @param customConfig The custom locale configuration to temporarily use.
     * @param callback The callback to execute with the custom locale configuration.
     *
     * @returns The result of the callback.
     */
    withLocaleConfig(
      customConfig: Partial<ILocale>,
      callback: (dayjsInstance: srcDayjs.Dayjs) => string,
    ): string;

    /**
     * Converts the date to a short-form human readable relative time format.
     *
     * It is strongly recommended to be used in conjunction with a `v-tooltip` containing the full timestamp.
     *
     * @example ```
     * <span v-tooltip="dayjs(createdAt).toString()">
     *  {{ dayjs(createdAt).short() }}
     * </span>
     * ```
     */
    short(): string;
  }
}

srcDayjs.extend(utc);
srcDayjs.extend(isoWeek);
srcDayjs.extend(timezone);
srcDayjs.extend(duration);
srcDayjs.extend(relativeTime);
srcDayjs.extend(advancedFormat);
srcDayjs.extend(customParseFormat);
srcDayjs.extend(updateLocale);
srcDayjs.extend(isBetween);

srcDayjs.extend((option, dayjsClass) => {
  dayjsClass.prototype.toLocalTime = function () {
    return this.tz(srcDayjs.tz.guess());
  };
});

srcDayjs.extend((option, dayjsClass) => {
  dayjsClass.prototype.withLocaleConfig = function (
    customConfig: Partial<ILocale>,
    callback: (dayjsInstance: srcDayjs.Dayjs) => string,
  ) {
    const currentLocale = this.locale();
    const oldConfig = { ...srcDayjs.Ls[currentLocale] };
    srcDayjs.updateLocale(currentLocale, {
      ...oldConfig,
      ...customConfig,
    });
    const result = callback(this);
    srcDayjs.updateLocale(currentLocale, oldConfig);
    return result;
  };
});

srcDayjs.extend((option, dayjsClass) => {
  dayjsClass.prototype.short = function () {
    return this.withLocaleConfig(
      {
        relativeTime: {
          future: 'in %s',
          past: '%s',
          s: 'now',
          m: '1m',
          mm: '%dm',
          h: '1h',
          hh: '%dh',
          d: '1d',
          dd: '%dd',
          M: '1mo',
          MM: '%dmo',
          y: '1y',
          yy: '%dy',
        },
      },
      (d) => d.fromNow(),
    );
  };
});

const dayjs = srcDayjs;
export { dayjs };
